/**
 * RedAlertModal - Full-screen new lead alert
 * Displays prominently when a new lead comes in via webhook
 */
import React, { useEffect, useState, useRef } from 'react';
import { 
  AlertTriangle, Phone, Mail, MapPin, Clock, DollarSign,
  X, ExternalLink, MessageSquare, CheckCircle2
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const RedAlertModal = ({ alert, onClose, onViewContact }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const audioRef = useRef(null);

  // Animate in on mount
  useEffect(() => {
    if (alert) {
      setIsVisible(true);
      setCountdown(30);
      
      // Play alert sound (optional - uncomment if desired)
      // try {
      //   audioRef.current = new Audio('/alert.mp3');
      //   audioRef.current.play().catch(() => {});
      // } catch {}
    }
  }, [alert]);

  // Auto-dismiss countdown
  useEffect(() => {
    if (!alert || countdown <= 0) return;
    
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          handleClose();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [alert, countdown]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleViewContact = () => {
    if (onViewContact && alert?.contact_id) {
      onViewContact(alert.contact_id);
    }
    handleClose();
  };

  if (!alert) return null;

  const {
    first_name,
    last_name,
    phone_display,
    phone,
    email,
    source_display,
    service_display,
    service_description,
    budget_display,
    timeline,
    address,
    auto_response_sent,
    auto_response_type,
    ai_score,
    ai_score_label,
  } = alert;

  const fullName = `${first_name || ''} ${last_name || ''}`.trim() || 'New Lead';

  // Score badge color
  const getScoreColor = () => {
    if (!ai_score) return 'bg-slate-600';
    if (ai_score >= 70) return 'bg-emerald-600';
    if (ai_score >= 40) return 'bg-yellow-600';
    return 'bg-slate-600';
  };

  return (
    <div 
      className={`fixed inset-0 z-[100] flex items-center justify-center transition-all duration-300 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      {/* Backdrop with pulsing effect */}
      <div 
        className="absolute inset-0 bg-red-950/95 backdrop-blur-sm animate-pulse-slow"
        onClick={handleClose}
      />
      
      {/* Alert content */}
      <div 
        className={`relative z-10 w-full max-w-2xl mx-4 transform transition-all duration-300 ${
          isVisible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
        }`}
      >
        {/* Header strip */}
        <div className="bg-red-600 rounded-t-2xl px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <AlertTriangle className="h-8 w-8 text-white animate-bounce" />
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-white rounded-full animate-ping" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white tracking-tight">NEW LEAD ALERT</h2>
              <p className="text-red-200 text-sm">via {source_display}</p>
            </div>
          </div>
          
          {/* Countdown & close */}
          <div className="flex items-center gap-4">
            <div className="text-center">
              <span className="text-3xl font-mono font-bold text-white">{countdown}</span>
              <p className="text-red-200 text-xs">seconds</p>
            </div>
            <button 
              onClick={handleClose}
              className="p-2 hover:bg-red-700 rounded-lg transition-colors"
            >
              <X className="h-6 w-6 text-white" />
            </button>
          </div>
        </div>

        {/* Main content */}
        <div className="bg-slate-900 rounded-b-2xl border-x-2 border-b-2 border-red-600/50">
          {/* Contact info */}
          <div className="p-6 border-b border-slate-800">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-3xl font-bold text-white mb-2">{fullName}</h3>
                <div className="flex flex-wrap gap-3">
                  {phone_display && (
                    <a 
                      href={`tel:${phone}`}
                      className="flex items-center gap-2 text-emerald-400 hover:text-emerald-300 transition-colors"
                    >
                      <Phone className="h-4 w-4" />
                      <span className="font-mono">{phone_display}</span>
                    </a>
                  )}
                  {email && (
                    <a 
                      href={`mailto:${email}`}
                      className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      <Mail className="h-4 w-4" />
                      <span>{email}</span>
                    </a>
                  )}
                </div>
              </div>
              
              {/* AI Score */}
              {ai_score !== undefined && ai_score !== null && (
                <div className="text-center">
                  <div className={`w-16 h-16 rounded-full ${getScoreColor()} flex items-center justify-center`}>
                    <span className="text-2xl font-bold text-white">{ai_score}</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1 capitalize">
                    {ai_score_label?.replace('_', ' ') || 'Score'}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Details grid */}
          <div className="p-6 grid grid-cols-2 gap-4">
            {service_display && service_display !== 'General Inquiry' && (
              <div className="bg-slate-800/50 rounded-lg p-4">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Service</p>
                <p className="text-white font-medium">{service_display}</p>
              </div>
            )}
            
            {budget_display && budget_display !== 'Not specified' && (
              <div className="bg-slate-800/50 rounded-lg p-4">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Budget</p>
                <p className="text-emerald-400 font-medium flex items-center gap-1">
                  <DollarSign className="h-4 w-4" />
                  {budget_display}
                </p>
              </div>
            )}
            
            {timeline && (
              <div className="bg-slate-800/50 rounded-lg p-4">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Timeline</p>
                <p className="text-white font-medium flex items-center gap-1 capitalize">
                  <Clock className="h-4 w-4 text-slate-400" />
                  {timeline.replace('_', ' ')}
                </p>
              </div>
            )}
            
            {address && (
              <div className="bg-slate-800/50 rounded-lg p-4">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Location</p>
                <p className="text-white font-medium flex items-center gap-1">
                  <MapPin className="h-4 w-4 text-slate-400" />
                  {address}
                </p>
              </div>
            )}
          </div>

          {/* Description if available */}
          {service_description && (
            <div className="px-6 pb-4">
              <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Message</p>
                <p className="text-slate-300 text-sm leading-relaxed line-clamp-3">
                  "{service_description}"
                </p>
              </div>
            </div>
          )}

          {/* Auto-response status */}
          <div className="px-6 pb-4">
            <div className={`flex items-center gap-2 p-3 rounded-lg ${
              auto_response_sent 
                ? 'bg-emerald-900/30 border border-emerald-600/30' 
                : 'bg-yellow-900/30 border border-yellow-600/30'
            }`}>
              {auto_response_sent ? (
                <>
                  <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                  <span className="text-emerald-300 text-sm">
                    Auto-response sent via {auto_response_type?.toUpperCase() || 'SMS'}
                  </span>
                </>
              ) : (
                <>
                  <MessageSquare className="h-5 w-5 text-yellow-400" />
                  <span className="text-yellow-300 text-sm">
                    No auto-response sent (missing contact info)
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="p-6 pt-2 flex gap-3">
            <Button 
              onClick={handleViewContact}
              className="flex-1 bg-red-600 hover:bg-red-500 text-white h-12 text-lg font-semibold"
              data-testid="red-alert-view-contact"
            >
              <ExternalLink className="h-5 w-5 mr-2" />
              View Contact
            </Button>
            <Button 
              variant="outline"
              onClick={handleClose}
              className="px-6 h-12 border-slate-600 text-slate-300 hover:bg-slate-800"
              data-testid="red-alert-dismiss"
            >
              Dismiss
            </Button>
          </div>
        </div>
      </div>

      {/* Keyframe animation for slow pulse */}
      <style>{`
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.95; }
          50% { opacity: 0.85; }
        }
        .animate-pulse-slow {
          animation: pulse-slow 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default RedAlertModal;
